<script setup>
/**
 * One menu in the list — `TaskCard`'s row grammar exactly: [mark] [what · when it shows · meta]
 * [state + switch] [edit]. The switch is on the row for the same reason: turning a menu off is
 * the hurried thing, and the write is a genuine partial patch (`{ enabled }` alone), so it
 * cannot clobber a draft open in another tab.
 */
import { computed } from 'vue';

import Switch from 'dashboard/components-next/switch/Switch.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import ClaudeChip from './ClaudeChip.vue';
import { useWasloI18n } from '../i18n.js';
import { MARKER, isolateParts } from '../helpers/bidi.js';
import { relativeTime } from '../helpers/relative-time.js';
import { toIntlLocale } from '../helpers/intl.js';

const props = defineProps({
  /** A row from `wasloApi.getMenus`. */
  menu: { type: Object, required: true },
  accountId: { type: Number, default: null },
  /** True while this row's own switch is being written. */
  isSaving: { type: Boolean, default: false },
  /** Bumped by the parent after a failed write, to snap `Switch` back (see TaskCard). */
  switchEpoch: { type: Number, default: 0 },
  /** E2 (P-8.4): Claude drafted it and nobody has edited it or switched it on since. */
  suggestedByClaude: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle']);

const { t, locale } = useWasloI18n();

/** The owner's own words inside a translated frame: a real bidi isolate, never one string. */
const shownWhen = computed(() =>
  isolateParts(
    t('WASLO.MENUS.LIST.WHEN', { when: MARKER }),
    props.menu.whenToShow
  )
);

const metaLine = computed(() => {
  const options = t('WASLO.MENUS.LIST.OPTIONS', {
    count: new Intl.NumberFormat(toIntlLocale(locale.value)).format(
      props.menu.optionCount
    ),
  });
  const when = relativeTime(props.menu.updatedAt, locale.value, {
    justNow: t('WASLO.MENUS.LIST.UPDATED_JUST_NOW'),
  });
  return [options, when ? t('WASLO.MENUS.LIST.UPDATED', { when }) : '']
    .filter(Boolean)
    .join(' · ');
});

/** P-5.9: customers shown this menu in the last 7 days. Nothing when not counted. */
const usageLine = computed(() => {
  const usage = props.menu.usage7d;
  if (!usage) return '';
  return t(
    'WASLO.MENUS.LIST.USAGE_SHOWN',
    {
      count: new Intl.NumberFormat(toIntlLocale(locale.value)).format(
        usage.conversations
      ),
    },
    usage.conversations
  );
});

const mark = computed(() =>
  props.menu.enabled
    ? { tile: 'bg-n-blue-3', icon: 'text-n-blue-10' }
    : { tile: 'bg-n-alpha-2', icon: 'text-n-slate-10' }
);

const to = computed(() =>
  props.accountId === null
    ? null
    : frontendURL(`accounts/${props.accountId}/waslo/menus/${props.menu.id}`)
);

const onToggle = next => emit('toggle', next);
</script>

<template>
  <li
    class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center"
    :data-waslo-menu="menu.id"
    :data-waslo-menu-enabled="menu.enabled"
  >
    <span
      class="grid rounded-lg shrink-0 place-items-center size-9"
      :class="mark.tile"
      aria-hidden="true"
    >
      <span class="size-4 i-lucide-list" :class="mark.icon" />
    </span>

    <div class="flex flex-col min-w-0 gap-1">
      <p
        class="mb-0 text-sm font-medium break-words text-n-slate-12"
        data-waslo-menu-title
      >
        <bdi>{{ menu.title }}</bdi>
      </p>
      <ClaudeChip v-if="suggestedByClaude" />
      <p
        class="mb-0 text-sm break-words text-n-slate-11 line-clamp-2"
        dir="auto"
        data-waslo-menu-when
      >
        <span>{{ shownWhen.before }}</span
        ><bdi>{{ shownWhen.value }}</bdi
        ><span>{{ shownWhen.after }}</span>
      </p>
      <p
        v-if="metaLine"
        class="mb-0 text-xs text-n-slate-10"
        data-waslo-menu-meta
      >
        {{ metaLine }}
      </p>
      <p
        v-if="usageLine"
        class="mb-0 text-xs text-n-slate-10"
        data-waslo-menu-usage
      >
        {{ usageLine }}
      </p>
    </div>

    <div class="flex items-center gap-3 shrink-0 sm:ms-auto ms-0">
      <span class="text-sm text-n-slate-11" data-waslo-menu-state>
        {{
          isSaving
            ? t('WASLO.MENUS.EDITOR.STATUS.SAVING')
            : menu.enabled
              ? t('WASLO.MENUS.LIST.ON')
              : t('WASLO.MENUS.LIST.OFF')
        }}
      </span>
      <Switch
        :key="switchEpoch"
        :model-value="menu.enabled"
        :aria-label="menu.title"
        data-waslo-menu-switch
        @update:model-value="onToggle"
      />
      <router-link
        v-if="to"
        :to="to"
        class="inline-flex items-center h-8 gap-1 px-3 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4"
        data-waslo-menu-edit
      >
        {{ t('WASLO.MENUS.LIST.EDIT') }}
      </router-link>
    </div>
  </li>
</template>
